import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { A11yModule } from '@angular/cdk/a11y';
import { ProcessingMetadata, RunCost } from '../../types/document-metadata';

const ROUTING_OUTCOME_LABELS: Record<string, string> = {
  'auto-selected': 'Auto-selected',
  'below-gate': 'Below gate',
  'near-tie': 'Near tie',
  'dead-end': 'Dead end',
  'no-tree': 'No tree'
};

const VERDICT_LABELS: Record<string, string> = {
  pass: 'Pass',
  needs_review: 'Needs review',
  fail: 'Fail'
};

/** Narrows to a value that can be formatted without producing `NaN`/`Infinity`. */
function isNumeric(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function formatCount(value: unknown): string | null {
  return isNumeric(value) ? value.toLocaleString('en-US') : null;
}

function formatCost(value: unknown): string | null {
  return isNumeric(value) ? `$${value.toFixed(4)}` : null;
}

function formatPercent(value: unknown): string | null {
  return isNumeric(value) ? `${Math.round(value * 100)}%` : null;
}

/**
 * Performance icon that opens a popover with the run's facts; renders nothing for an un-instrumented
 * document.
 *
 * Two inputs because the halves have different lifetimes: `facts` is stored on the
 * document, while `cost` is priced by the processing-metadata endpoint and so is
 * absent on the GraphQL list and subscription path. The panel then renders the run
 * without a money section rather than waiting for one.
 *
 * Neither input triggers a request of its own.
 */
@Component({
  selector: 'app-ocr-facts',
  templateUrl: './ocr-facts.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, MatTooltipModule, OverlayModule, A11yModule]
})
export class OcrFactsComponent {
  /** Absent for un-instrumented documents. */
  readonly facts = input<ProcessingMetadata | null | undefined>(null);

  /** Absent on transports that do not price, and for a run that spent nothing. */
  readonly cost = input<RunCost | null | undefined>(null);

  readonly hasContent = computed(() => !!this.facts());

  readonly open = signal(false);

  readonly popoverPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 }
  ];

  readonly durationSeconds = computed(() => {
    const ms = this.facts()?.durationMs;
    return isNumeric(ms) ? `${(ms / 1000).toFixed(1)} s` : null;
  });

  /** Only a parseable timestamp is handed to the date pipe. */
  readonly completedAt = computed(() => {
    const value = this.facts()?.completedAt;
    if (typeof value !== 'string' || !value.trim()) return null;
    return Number.isNaN(Date.parse(value)) ? null : value;
  });

  readonly fixAttempts = computed(() => formatCount(this.facts()?.fixAttempts));

  readonly verdict = computed(() => {
    const verdict = this.facts()?.verdict;
    return verdict ? VERDICT_LABELS[verdict] ?? verdict : null;
  });

  readonly judgeConfidence = computed(() => formatPercent(this.facts()?.judgeConfidence));

  readonly tokens = computed(() => {
    const tokens = this.cost()?.tokens;
    return {
      input: formatCount(tokens?.input),
      cachedInput: formatCount(tokens?.cachedInput),
      output: formatCount(tokens?.output),
      reasoning: formatCount(tokens?.reasoning),
      total: formatCount(tokens?.total),
      requests: formatCount(tokens?.requests)
    };
  });

  /** A reprocessed document converts nothing, so 0 is a real reading rather than missing data. */
  readonly pagesConverted = computed(() => formatCount(this.cost()?.pagesConverted));

  readonly costLabel = computed(() => formatCost(this.cost()?.costUsd));

  readonly costByModelRows = computed(() =>
    Object.entries(this.cost()?.costByModel ?? {})
      .filter(([, costUsd]) => isNumeric(costUsd))
      .map(([modelId, costUsd]) => ({ modelId, costLabel: `$${costUsd.toFixed(4)}` }))
      .sort((a, b) => a.modelId.localeCompare(b.modelId)));

  readonly templateMatchScore = computed(() => formatPercent(this.facts()?.templateMatchScore));

  readonly routingOutcome = computed(() => {
    const outcome = this.facts()?.routingOutcome;
    return outcome ? ROUTING_OUTCOME_LABELS[outcome] ?? outcome : null;
  });

  /** A property of the reading, not of the run: a re-read after a correction reports a new revision. */
  readonly rateCardRevision = computed(() => {
    const revision = this.cost()?.rateCardRevision;
    return typeof revision === 'string' && revision.trim() ? revision : null;
  });

  readonly summary = computed(() =>
    ['Performance', this.durationSeconds(), this.costLabel()].filter(Boolean).join(' · '));

  toggle(): void {
    this.open.update(isOpen => !isOpen);
  }

  close(): void {
    this.open.set(false);
  }
}
